import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  FormControlLabel,
  Paper,
  Snackbar,
  Switch,
  TextField,
  Typography,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { authGet, authPut, authDelete, AuthRequest } from "../../lib";
import { REACT_APP_API_URL } from "../../config";

/**
 * REACT_APP_API_URL carries an /api/V1 suffix in some environments and not in
 * others; the static mount is at the server root, so strip it rather than
 * assume either way. Same reasoning as PreownedImages.
 */
const API_ORIGIN = String(REACT_APP_API_URL || "").replace(/\/api\/v\d+\/?$/i, "");
const IMAGE_BASE = `${API_ORIGIN}/home-slider-images`;

/**
 * The four home page strips. They share a table, an endpoint and this screen;
 * only the labels, the fields worth showing and the image advice differ.
 *
 * `dates` is deliberately slider-only: a scheduling window makes sense for a
 * seasonal banner and is noise on a brand logo.
 */
export const BLOCK_KINDS = {
  slider: {
    title: "Main Slider",
    blurb:
      "Rotating images across the top of the home page. Lowest sort order " +
      "shows first. Leave the dates empty for a slide that runs until you " +
      "turn it off.",
    headingLabel: "Caption",
    linkHint: "/watches.html",
    fields: ["heading", "link", "alt_text", "dates"],
    hint: "Wide images work best — roughly 1600×400.",
  },
  brand: {
    title: "Brand Tiles",
    blurb:
      "The large tiles under Authorized Dealer. The image is the product " +
      "shot; the name shows on the band beneath it, and both link to where " +
      "you point them.",
    headingLabel: "Brand name",
    linkHint: "/omega.html",
    fields: ["heading", "link", "alt_text"],
    hint: "Portrait product shots, roughly 600×700. Three fill the row.",
    // The storefront lays these out three across.
    idealCount: 3,
  },
  logo: {
    title: "Logo Strip",
    blurb:
      "The scrolling brand logos beneath the tiles. Order sets the sequence; " +
      "the strip loops seamlessly however many you add.",
    headingLabel: "Brand name",
    linkHint: "/omega.html",
    fields: ["heading", "link"],
    hint: "Transparent PNG logos, roughly 200×60.",
  },
  collection: {
    title: "Featured Collections",
    blurb:
      "The cards below the logos. The caption sits under the image, and the " +
      "link is where both it and See Collection go.",
    headingLabel: "Caption",
    linkHint: "/omega.html?series_group=speedmaster",
    fields: ["heading", "link", "alt_text"],
    hint: "Landscape shots, roughly 800×600.",
  },
};

const useStyles = makeStyles((theme) => ({
  page: { maxWidth: 1000, margin: "24px auto 0", width: "100%" },
  panel: { padding: theme.spacing(2.5), marginBottom: theme.spacing(2) },
  title: { fontSize: 18, fontWeight: 500 },
  blurb: {
    color: theme.palette.text.secondary,
    margin: theme.spacing(0.75, 0, 2.5),
    maxWidth: 760,
  },
  sectionHead: {
    display: "flex",
    alignItems: "baseline",
    justifyContent: "space-between",
    marginBottom: theme.spacing(2),
  },
  count: { color: theme.palette.text.secondary, fontSize: 13 },
  warn: { color: "#a06000", fontSize: 13 },

  // Add form and each saved block share the same column rhythm, so the two
  // halves of the page line up instead of looking like separate screens.
  form: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "flex-end",
    gap: theme.spacing(1.5),
  },
  heading: { flex: "1 1 200px", minWidth: 160 },
  link: { flex: "1 1 240px", minWidth: 160 },
  alt: { flex: "1 1 180px", minWidth: 150 },
  order: { flex: "0 0 88px" },
  date: { flex: "0 0 150px" },
  /** Zero-height full-width item: forces the fields that follow onto a new line. */
  lineBreak: { flexBasis: "100%", height: 0, margin: 0 },

  picker: {
    flex: "none",
    width: 148,
    height: 72,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    textAlign: "center",
    fontSize: 12,
    lineHeight: 1.3,
    padding: theme.spacing(0, 1),
    color: theme.palette.text.secondary,
    border: "1px dashed rgba(0,0,0,0.28)",
    borderRadius: 3,
    cursor: "pointer",
    overflow: "hidden",
    transition: "border-color .15s, background-color .15s",
    "&:hover": {
      borderColor: theme.palette.primary.main,
      backgroundColor: "rgba(49,81,121,0.04)",
    },
  },
  pickerFilled: { borderStyle: "solid", padding: 0 },
  pickerDrag: {
    borderColor: theme.palette.primary.main,
    backgroundColor: "rgba(49,81,121,0.08)",
  },
  preview: { width: "100%", height: "100%", objectFit: "contain", display: "block" },
  addBtn: { height: 40, flex: "none", paddingLeft: 20, paddingRight: 20 },

  block: {
    display: "flex",
    alignItems: "flex-start",
    gap: theme.spacing(2),
    padding: theme.spacing(2),
    marginBottom: theme.spacing(1.5),
    borderRadius: 4,
    "&:last-child": { marginBottom: 0 },
  },
  blockOff: { backgroundColor: "rgba(0,0,0,0.02)" },

  // The thumbnail doubles as the file picker for swapping the image, so the
  // control sits where the thing it changes already is.
  swap: {
    position: "relative",
    width: 190,
    // Matches the actions column beside it (switch + Save + Delete), so the
    // row reads as one band rather than three ragged heights.
    height: 108,
    flex: "none",
    display: "block",
    cursor: "pointer",
    "&:hover $swapHint": { opacity: 1 },
  },
  thumb: {
    width: "100%",
    height: "100%",
    // contain, not cover: these are product shots of varying aspect, and
    // cropping to fill zooms into the middle of the watch.
    objectFit: "contain",
    display: "block",
    borderRadius: 3,
    background: "#f7f7f7",
    border: "1px solid rgba(0,0,0,0.12)",
  },
  thumbOff: { opacity: 0.45, filter: "grayscale(1)" },
  // The hint is normally hover-only; a drag has no hover, so force it visible.
  swapDrag: {
    "& $thumb": {
      borderColor: theme.palette.primary.main,
      backgroundColor: "rgba(49,81,121,0.08)",
    },
    "& $swapHint": { opacity: 1 },
  },
  swapHint: {
    position: "absolute",
    left: 0,
    right: 0,
    bottom: 0,
    padding: "3px 0",
    textAlign: "center",
    fontSize: 11,
    color: "#fff",
    background: "rgba(0,0,0,0.6)",
    borderRadius: "0 0 3px 3px",
    opacity: 0,
    transition: "opacity .15s",
  },

  fields: {
    flex: 1,
    minWidth: 0,
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(1.5),
  },
  actions: {
    flex: "none",
    display: "flex",
    flexDirection: "column",
    alignItems: "stretch",
    gap: theme.spacing(1),
    width: 104,
  },
  toggle: { margin: 0, justifyContent: "flex-start" },
  toggleLabel: { fontSize: 12 },
  // MUI v4 has no error-coloured Button variant; without a border the Delete
  // read as disabled next to Save.
  delete: {
    color: "#c62828",
    border: "1px solid rgba(198,40,40,0.4)",
    backgroundColor: "rgba(198,40,40,0.04)",
    "&:hover": {
      backgroundColor: "rgba(198,40,40,0.1)",
      borderColor: "#c62828",
    },
  },
  empty: {
    padding: theme.spacing(4, 2),
    textAlign: "center",
    color: theme.palette.text.secondary,
  },
  error: { color: "#c62828", marginTop: theme.spacing(1.5) },
  hint: {
    color: theme.palette.text.secondary,
    fontSize: 12,
    marginTop: theme.spacing(1.5),
  },
}));

const BLANK = { heading: "", link: "", alt_text: "", starts_at: "", ends_at: "" };

/** Mirrors the accept attribute and the server's magic-byte check. */
const ACCEPT = ["image/jpeg", "image/png", "image/webp"];

/** Pulls a usable image off a drop, or says why it cannot. */
function droppedImage(e) {
  const file = e.dataTransfer?.files?.[0];
  if (!file) return { error: "Drop an image file." };
  if (!ACCEPT.includes(file.type)) {
    return { error: "Drop a JPG, PNG or WEBP image." };
  }
  return { file };
}

/**
 * Manage Site > Home Page > one of the four strips.
 *
 * Every strip is image + text + link + order, so one screen serves all of
 * them; `kind` decides the labels and which fields are worth showing.
 */
export function HomePageBlocksPage({ type = "slider" }) {
  const classes = useStyles();
  const kind = BLOCK_KINDS[type] ?? BLOCK_KINDS.slider;
  const shows = (f) => kind.fields.includes(f);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [pending, setPending] = useState(BLANK);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState("");
  // Which zone the pointer is currently over: "new", or a block_id.
  const [dragOver, setDragOver] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    const { data, error: err } = await authGet(`/home-blocks?type=${type}`);
    if (err) setError(err.message || "Could not load this section.");
    else setRows(data?.rows ?? []);
    setLoading(false);
  }, [type]);

  useEffect(() => {
    load();
  }, [load]);

  // Switching sections must not carry the previous one's draft across.
  useEffect(() => {
    setPending(BLANK);
    setFile(null);
    setError("");
  }, [type]);

  useEffect(() => {
    if (!notice) return undefined;
    const t = setTimeout(() => setNotice(""), 3500);
    return () => clearTimeout(t);
  }, [notice]);

  // A drop that misses a zone otherwise makes the browser open the file,
  // throwing away whatever was typed into the form.
  useEffect(() => {
    const swallow = (e) => e.preventDefault();
    document.addEventListener("dragover", swallow);
    document.addEventListener("drop", swallow);
    return () => {
      document.removeEventListener("dragover", swallow);
      document.removeEventListener("drop", swallow);
    };
  }, []);

  // Object URLs leak until revoked, and one is created per pick.
  useEffect(() => {
    if (!file) {
      setPreview("");
      return undefined;
    }
    const url = URL.createObjectURL(file);
    setPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  async function add() {
    if (!file) {
      setError("Choose an image first.");
      return;
    }
    setBusy(true);
    setError("");

    const body = new FormData();
    // Text before the file — multer parses in stream order.
    Object.entries(pending).forEach(([k, v]) => v && body.append(k, v));
    body.append("afile", file);

    try {
      await AuthRequest.post(`/home-blocks?type=${type}`, body, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPending(BLANK);
      setFile(null);
      setNotice("Added.");
      await load();
    } catch (e) {
      setError(e?.response?.data?.message || e.message || "Upload failed");
    }
    setBusy(false);
  }

  async function swapImage(row, picked) {
    if (!picked) return;
    setBusy(true);
    setError("");

    const body = new FormData();
    body.append("afile", picked);

    try {
      await AuthRequest.post(`/home-blocks/${row.block_id}/image`, body, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setNotice("Image replaced.");
      await load();
    } catch (e) {
      setError(e?.response?.data?.message || e.message || "Upload failed");
    }
    setBusy(false);
  }

  async function save(row) {
    setBusy(true);
    const { error: err } = await authPut(`/home-blocks/${row.block_id}`, {
      heading: row.heading || null,
      link: row.link || null,
      alt_text: row.alt_text || null,
      sort_order: Number(row.sort_order) || 0,
      active: !!row.active,
      starts_at: row.starts_at || null,
      ends_at: row.ends_at || null,
    });
    setBusy(false);
    if (err) setError(err.message || "Save failed");
    else setNotice("Saved.");
  }

  async function remove(row) {
    if (!window.confirm("Delete this? The image file is removed too.")) return;
    setBusy(true);
    const { error: err } = await authDelete(`/home-blocks/${row.block_id}`);
    setBusy(false);
    if (err) setError(err.message || "Delete failed");
    else {
      setNotice("Deleted.");
      load();
    }
  }

  const edit = (id, field) => (e) => {
    const v = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setRows((prev) => prev.map((r) => (r.block_id === id ? { ...r, [field]: v } : r)));
  };

  const set = (f) => (e) => setPending((p) => ({ ...p, [f]: e.target.value }));

  /** Drag-and-drop wiring for one zone, keyed so only that one highlights. */
  const dropZone = (key, onFile) => ({
    onDragOver: (e) => {
      e.preventDefault();
      e.dataTransfer.dropEffect = "copy";
    },
    onDragEnter: (e) => {
      e.preventDefault();
      setDragOver(key);
    },
    onDragLeave: (e) => {
      // Also fires when crossing onto a child element, which is not a leave.
      if (!e.currentTarget.contains(e.relatedTarget)) {
        setDragOver((k) => (k === key ? null : k));
      }
    },
    onDrop: (e) => {
      e.preventDefault();
      setDragOver(null);
      const { file: dropped, error: err } = droppedImage(e);
      if (err) {
        setError(err);
        return;
      }
      setError("");
      onFile(dropped);
    },
  });

  const liveCount = rows.filter((r) => r.active).length;
  const short = kind.idealCount && !loading && rows.length > 0 && liveCount !== kind.idealCount;

  return (
    <Box className={classes.page}>
      <Paper className={classes.panel}>
        <Typography className={classes.title}>{kind.title}</Typography>
        <Typography variant="body2" className={classes.blurb}>
          {kind.blurb}
        </Typography>

        <div className={classes.form}>
          <label
            className={[
              classes.picker,
              file ? classes.pickerFilled : "",
              dragOver === "new" ? classes.pickerDrag : "",
            ].join(" ")}
            htmlFor="block-file"
            {...dropZone("new", setFile)}
          >
            {preview ? (
              <img className={classes.preview} src={preview} alt="" />
            ) : (
              <span>{dragOver === "new" ? "Drop to upload" : "Choose image or drop one here"}</span>
            )}
            <input
              id="block-file"
              type="file"
              accept="image/jpeg,image/png,image/webp"
              hidden
              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            />
          </label>

          <TextField
            className={classes.heading}
            label={kind.headingLabel}
            value={pending.heading}
            onChange={set("heading")}
          />
          <TextField
            className={classes.link}
            label="Link"
            placeholder={kind.linkHint}
            value={pending.link}
            onChange={set("link")}
          />
          {shows("alt_text") && (
            <>
              <span className={classes.lineBreak} />
              <TextField
                className={classes.alt}
                label="Alt text"
                value={pending.alt_text}
                onChange={set("alt_text")}
              />
            </>
          )}
          <Button
            className={classes.addBtn}
            variant="contained"
            color="primary"
            onClick={add}
            disabled={busy}
          >
            Add
          </Button>
        </div>

        <Typography variant="body2" className={classes.hint}>
          {kind.hint}
          {file ? ` Selected: ${file.name}` : ""}
        </Typography>

        {error && (
          <Typography variant="body2" className={classes.error}>
            {error}
          </Typography>
        )}
      </Paper>

      <Paper className={classes.panel}>
        <div className={classes.sectionHead}>
          <Typography className={classes.title}>In this section</Typography>
          {!loading && rows.length > 0 && (
            <span className={short ? classes.warn : classes.count}>
              {liveCount} of {rows.length} showing
              {short ? ` — the row fits ${kind.idealCount}` : ""}
            </span>
          )}
        </div>

        {loading ? (
          <Typography variant="body2">Loading…</Typography>
        ) : !rows.length ? (
          <div className={classes.empty}>
            <Typography variant="body2">
              Nothing here yet. The storefront falls back to its built-in list
              until you add the first one.
            </Typography>
          </div>
        ) : (
          rows.map((row) => (
            <Paper
              variant="outlined"
              className={`${classes.block} ${row.active ? "" : classes.blockOff}`}
              key={row.block_id}
            >
              <label
                className={`${classes.swap} ${dragOver === row.block_id ? classes.swapDrag : ""}`}
                {...dropZone(row.block_id, (dropped) => swapImage(row, dropped))}
              >
                <img
                  className={`${classes.thumb} ${row.active ? "" : classes.thumbOff}`}
                  alt={row.alt_text || row.heading || kind.title}
                  src={`${IMAGE_BASE}/${row.image}`}
                />
                <span className={classes.swapHint}>
                  {dragOver === row.block_id ? "Drop to replace" : "Change image"}
                </span>
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  hidden
                  onChange={(e) => {
                    swapImage(row, e.target.files?.[0]);
                    // Lets the same file be picked again after a failure.
                    e.target.value = "";
                  }}
                />
              </label>

              <div className={classes.fields}>
                <TextField
                  className={classes.heading}
                  label={kind.headingLabel}
                  value={row.heading ?? ""}
                  onChange={edit(row.block_id, "heading")}
                />
                <TextField
                  className={classes.link}
                  label="Link"
                  placeholder={kind.linkHint}
                  value={row.link ?? ""}
                  onChange={edit(row.block_id, "link")}
                />
                {/* Alt drops to a second line with Order, keeping the first
                    line to the two fields that are edited most. */}
                {shows("alt_text") && (
                  <>
                    <span className={classes.lineBreak} />
                    <TextField
                      className={classes.alt}
                      label="Alt text"
                      value={row.alt_text ?? ""}
                      onChange={edit(row.block_id, "alt_text")}
                    />
                  </>
                )}
                <TextField
                  className={classes.order}
                  label="Order"
                  type="number"
                  value={row.sort_order ?? 0}
                  onChange={edit(row.block_id, "sort_order")}
                />
                {shows("dates") && (
                  <>
                    <TextField
                      className={classes.date}
                      label="Starts"
                      type="date"
                      value={(row.starts_at ?? "").slice(0, 10)}
                      onChange={edit(row.block_id, "starts_at")}
                    />
                    <TextField
                      className={classes.date}
                      label="Ends"
                      type="date"
                      value={(row.ends_at ?? "").slice(0, 10)}
                      onChange={edit(row.block_id, "ends_at")}
                    />
                  </>
                )}
              </div>

              <div className={classes.actions}>
                <FormControlLabel
                  className={classes.toggle}
                  classes={{ label: classes.toggleLabel }}
                  control={
                    <Switch
                      size="small"
                      color="primary"
                      checked={!!row.active}
                      onChange={edit(row.block_id, "active")}
                    />
                  }
                  label={row.active ? "Active" : "Hidden"}
                />
                <Button
                  size="small"
                  variant="contained"
                  color="primary"
                  onClick={() => save(row)}
                  disabled={busy}
                >
                  Save
                </Button>
                <Button
                  size="small"
                  className={classes.delete}
                  onClick={() => remove(row)}
                  disabled={busy}
                >
                  Delete
                </Button>
              </div>
            </Paper>
          ))
        )}
      </Paper>

      <Snackbar
        open={!!notice}
        autoHideDuration={3500}
        onClose={() => setNotice("")}
        message={notice}
      />
    </Box>
  );
}

// One route each, so the sidebar can name them and a link is bookmarkable.
export const HomeSliderPage = () => <HomePageBlocksPage type="slider" />;
export const HomeBrandTilesPage = () => <HomePageBlocksPage type="brand" />;
export const HomeLogoStripPage = () => <HomePageBlocksPage type="logo" />;
export const HomeCollectionsPage = () => <HomePageBlocksPage type="collection" />;

export default HomePageBlocksPage;
